import { FormEvent, useEffect, useMemo } from 'react';

import { TagDTO, TagEntity, UpsertUnitsRequestDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import { SaveChangesDialog } from '@/components/common/save-changes-dialog';
import DeleteIcon from '@/components/icons/delete';
import QuestionIcon from '@/components/icons/question';
import { ScheduleSettingsSkeleton } from '@/components/settings/settings-form-skeleton';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipArrow, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/hooks/use-permissions';
import { useSaveChangesGuard } from '@/hooks/use-save-changes-guard';
import { getTagsQueryOptions, tagKeys } from '@/queries/assets';
import { currentUserKeys } from '@/queries/current-user';
import { getUnitsQueryOptions, unitKeys } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';

import ScheduleCheckTags from './schedule-check-tags';

const unitsFormSchema = z.object({
  units: z.array(
    z.object({
      id: z.string(),
      name: z.string().min(1, 'Required'),
      abbreviation: z.string().min(1, 'Required'),
      formCount: z.number().nullish(),
    }),
  ),
});

type UnitsFormData = z.infer<typeof unitsFormSchema>;

const scheduleTagsFormSchema = z.object({
  tags: z.array(z.object({ id: z.string(), tag: z.string(), order: z.number().nullable() })),
});

type ScheduleTagsFormData = z.infer<typeof scheduleTagsFormSchema>;

const autoCloseFormSchema = z.object({
  autoCloseChecksOnScheduleFormSubmit: z.boolean(),
});

type AutoCloseFormData = z.infer<typeof autoCloseFormSchema>;

type ScheduleSettingsSaveResult = { settingName: string; succeeded: boolean };

function SettingsSchedulesForm() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { canEditWorkspaceSettings } = usePermissions();
  const { org, setOrg } = useUserStore();

  const { data: activeUnits, isLoading: isLoadingUnits } = useQuery(getUnitsQueryOptions());

  const units = useMemo(() => activeUnits || [], [activeUnits]);

  const { data: scheduleTags } = useQuery(getTagsQueryOptions({ entity: TagEntity.Check }));

  const {
    control: unitsControl,
    handleSubmit: handleSubmitUnits,
    reset: resetUnits,
    formState: { isDirty: isUnitsDirty, isSubmitting: isUnitsSubmitting, errors: unitsErrors },
  } = useForm<UnitsFormData>({
    resolver: zodResolver(unitsFormSchema),
    defaultValues: { units: [] },
  });

  // Only units gate the dialog, so the guard reads units dirtiness alone — a tags-only or
  // toggle-only save short-circuits straight through.
  const { isConfirmOpen, confirmSafeToLeave, settleConfirm, onConfirmOpenChange } = useSaveChangesGuard(() => isUnitsDirty);

  const { fields, append, remove, replace } = useFieldArray({
    control: unitsControl,
    name: 'units',
  });

  const {
    watch: watchTags,
    setValue: setTagsValue,
    handleSubmit: handleSubmitTags,
    reset: resetTags,
    formState: { isDirty: isTagsDirty, isSubmitting: isTagsSubmitting },
  } = useForm<ScheduleTagsFormData>({
    resolver: zodResolver(scheduleTagsFormSchema),
    defaultValues: { tags: [] },
  });

  const tagsFormValues = watchTags();

  const {
    control: autoCloseControl,
    handleSubmit: handleSubmitAutoClose,
    reset: resetAutoClose,
    formState: { isDirty: isAutoCloseDirty, isSubmitting: isAutoCloseSubmitting },
  } = useForm<AutoCloseFormData>({
    resolver: zodResolver(autoCloseFormSchema),
    defaultValues: { autoCloseChecksOnScheduleFormSubmit: org?.autoCloseChecksOnScheduleFormSubmit ?? true },
  });

  // react-hook-form only clears `isDirty` on reset, not on submit, so reset once each
  // query refetches post-save or the Save button stays enabled.
  useEffect(() => {
    resetUnits({
      units: units.map((unit) => ({
        id: unit.id,
        name: unit.name,
        abbreviation: unit.abbreviation || '',
        formCount: unit.formCount || 0,
      })),
    });
  }, [units, resetUnits]);

  useEffect(() => {
    if (scheduleTags) {
      resetTags({ tags: scheduleTags });
    }
  }, [scheduleTags, resetTags]);

  const { mutateAsync: updateUnits, isPending: isUnitsPending } = useMutation({
    mutationFn: (payload: UpsertUnitsRequestDTO) => {
      return ApiService.UpsertUnits(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: unitKeys.list(false) });
    },
  });

  const { mutateAsync: updateScheduleTags, isPending: isTagsPending } = useMutation({
    mutationFn: async ({ tags }: { tags: TagDTO[] }) =>
      await ApiService.UpdateTags({
        tags,
        entity: TagEntity.Check,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.list(TagEntity.Check, false) });
    },
  });

  const { mutateAsync: updateAutoClose, isPending: isAutoClosePending } = useMutation({
    mutationFn: async (autoCloseChecksOnScheduleFormSubmit: boolean) => {
      if (!org) {
        throw new Error('Workspace is not loaded');
      }
      return (await (await ApiService.UpdateAutoCloseChecksOnScheduleFormSubmit({ workspaceId: org.id, autoCloseChecksOnScheduleFormSubmit })).json())
        .autoCloseChecksOnScheduleFormSubmit;
    },
  });

  const saveUnits = async (unitsToSave: UnitsFormData['units']): Promise<ScheduleSettingsSaveResult> => {
    try {
      await updateUnits({ units: unitsToSave });
      return { settingName: 'units', succeeded: true };
    } catch {
      return { settingName: 'units', succeeded: false };
    }
  };

  const saveTags = async (tags: TagDTO[]): Promise<ScheduleSettingsSaveResult> => {
    try {
      await updateScheduleTags({ tags: tags.map((tag, index) => ({ ...tag, order: index })) });
      return { settingName: 'Schedule Check tags', succeeded: true };
    } catch {
      return { settingName: 'Schedule Check tags', succeeded: false };
    }
  };

  const saveAutoClose = async (autoCloseChecksOnScheduleFormSubmit: boolean): Promise<ScheduleSettingsSaveResult> => {
    try {
      const result = await updateAutoClose(autoCloseChecksOnScheduleFormSubmit);
      if (org) {
        setOrg({ ...org, autoCloseChecksOnScheduleFormSubmit: result });
      }
      // The cached GetCurrentWebUser query backs AppBootstrap's own setOrg call, which
      // re-fires on every OIDC silent-renewal cycle. Without this, the next renewal
      // rehydrates `org` from the pre-save cached snapshot and clobbers the update above.
      queryClient.invalidateQueries({ queryKey: currentUserKeys.all });
      resetAutoClose({ autoCloseChecksOnScheduleFormSubmit: result });
      return { settingName: 'the auto-close setting', succeeded: true };
    } catch {
      return { settingName: 'the auto-close setting', succeeded: false };
    }
  };

  const isSavePending = isUnitsPending || isTagsPending || isAutoClosePending;

  const isSaveDisabled = isUnitsSubmitting || isTagsSubmitting || isAutoCloseSubmitting || (!isUnitsDirty && !isTagsDirty && !isAutoCloseDirty);

  const handleSave = async () => {
    let unitsData: UnitsFormData | undefined;
    let tagsData: ScheduleTagsFormData | undefined;
    let autoCloseData: AutoCloseFormData | undefined;

    /* Validate every dirty section before firing anything, so a rejected section can't leave a
       sibling endpoint already applied. Each section has its own resolver, so an undefined
       capture means that section's callback never ran. */
    if (isUnitsDirty) {
      await handleSubmitUnits((validated) => {
        unitsData = validated;
      })();
      if (!unitsData) return;
    }

    if (isTagsDirty) {
      await handleSubmitTags((validated) => {
        tagsData = validated;
      })();
      if (!tagsData) return;
    }

    if (isAutoCloseDirty) {
      await handleSubmitAutoClose((validated) => {
        autoCloseData = validated;
      })();
      if (!autoCloseData) return;
    }

    if (!unitsData && !tagsData && !autoCloseData) return;

    // Unit edits reach existing forms and assets, so they gate the whole batch: declining
    // here saves nothing, including the sections the dialog says nothing about.
    if (unitsData && !(await confirmSafeToLeave())) return;

    // Positional, so each section can mark itself saved on its own result.
    const [unitsResult, tagsResult, autoCloseResult] = await Promise.all([
      unitsData ? saveUnits(unitsData.units) : undefined,
      tagsData ? saveTags(tagsData.tags) : undefined,
      autoCloseData ? saveAutoClose(autoCloseData.autoCloseChecksOnScheduleFormSubmit) : undefined,
    ]);

    // TanStack reuses the list reference when the refetch is unchanged, so the reset effects can't be relied on.
    if (unitsData && unitsResult?.succeeded) {
      resetUnits(unitsData);
    }

    if (tagsData && tagsResult?.succeeded) {
      resetTags(tagsData);
    }

    const failed = [unitsResult, tagsResult, autoCloseResult].filter((result): result is ScheduleSettingsSaveResult => !!result && !result.succeeded);

    if (failed.length === 0) {
      toast.success('Schedule settings updated successfully');
      return;
    }

    // Named per setting rather than per section, because the three endpoints fail independently.
    toast.error(failed.length === 1 ? `Failed to update ${failed[0].settingName}` : 'Some changes could not be saved');
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSave();
  };

  function handleTagsChange(tags: TagDTO[]) {
    setTagsValue('tags', tags, { shouldDirty: true });
  }

  const handleAddUnit = () => {
    append({
      id: uuidv4(),
      name: '',
      abbreviation: '',
    });
  };

  const handleSortingChange = ({ key, direction }: { key?: string; direction?: 'asc' | 'desc' }) => {
    if (!key || !direction) return;

    const sortedFields = [...fields].sort((a, b) => {
      let aValue = a[key as keyof typeof a] || '';
      let bValue = b[key as keyof typeof b] || '';

      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    replace(sortedFields);
  };

  const columns = useMemo<AccessorKeyColumnDef<UnitsFormData['units'][number]>[]>(() => {
    const columnHelper = createColumnHelper<UnitsFormData['units'][number]>();

    return [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => {
          const index = fields.findIndex((field) => field.id === row.original.id);
          return (
            <Controller
              control={unitsControl}
              name={`units.${index}.name`}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <div className="h-full w-full flex-col items-start gap-1">
                  <Input
                    value={value}
                    onChange={onChange}
                    placeholder="Enter unit name..."
                    error={!!unitsErrors.units?.[index]?.name}
                    disabled={!canEditWorkspaceSettings}
                  />
                  <span className="text-default-red text-sm">{error?.message}</span>
                </div>
              )}
            />
          );
        },
      }),
      columnHelper.accessor('abbreviation', {
        id: 'abbreviation',
        header: 'Abbreviation',
        cell: ({ row }) => {
          const index = fields.findIndex((field) => field.id === row.original.id);
          return (
            <Controller
              control={unitsControl}
              name={`units.${index}.abbreviation`}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <div className="h-full w-full flex-col items-start gap-1">
                  <Input
                    value={value}
                    onChange={onChange}
                    placeholder="Enter abbreviation..."
                    error={!!unitsErrors.units?.[index]?.abbreviation}
                    disabled={!canEditWorkspaceSettings}
                  />
                  <span className="text-default-red text-sm">{error?.message}</span>
                </div>
              )}
            />
          );
        },
      }),
      columnHelper.display({
        id: 'formCount',
        header: 'Form Count',
        cell: ({ row }) => row.original.formCount || 0,
      }),
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const index = fields.findIndex((field) => field.id === row.original.id);
          return (
            <div className="h-full flex-col items-start">
              {canEditWorkspaceSettings && (
                <Button type="button" aria-label="Remove" className="bg-grey-1 hover:bg-grey-3 px-2" onClick={() => remove(index)}>
                  <DeleteIcon className="text-default-red" />
                </Button>
              )}
            </div>
          );
        },
      },
    ] as AccessorKeyColumnDef<UnitsFormData['units'][number]>[];
  }, [fields, unitsControl, unitsErrors, remove, canEditWorkspaceSettings]);

  if (isLoadingUnits || !scheduleTags) {
    return <ScheduleSettingsSkeleton />;
  }

  return (
    <>
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4">
        <h2 className="text-default-text text-sm">Units</h2>

        <div className="flex flex-col gap-2">
          <div className="rounded-lg border">
            <TablePageTable
              data={fields}
              columns={columns}
              notFoundDisplay={<p className="text-secondary-text p-2 text-sm">No units configured yet</p>}
              tableClassName="table-fixed [&_tr:last-child_td]:border-b-0 [&_tr:hover]:bg-inherit"
              cellClassName="border-x-transparent p-2"
              headClassName="!border-none first-of-type:rounded-tl-lg last-of-type:rounded-tr-lg"
              headerClassName="after:!hidden before:!hidden"
              notFoundDisplayContainerClassName="pointer-events-auto"
              sortButtonClassName="justify-start"
              onSortingChange={handleSortingChange}
            />
          </div>

          {canEditWorkspaceSettings && (
            <Button type="button" variant="default" className="w-fit" onClick={handleAddUnit}>
              Add Unit
            </Button>
          )}
        </div>

        <ScheduleCheckTags onTagsChange={handleTagsChange} scheduleTags={tagsFormValues.tags} disabled={!canEditWorkspaceSettings} />

        <div className="flex w-full items-center gap-4">
          <Label htmlFor="auto-close-schedule-checks" className="text-default-text w-[350px] shrink-0 text-sm font-normal">
            Auto-close schedule checks on submission
          </Label>
          <div className="flex items-center gap-2">
            <Controller
              control={autoCloseControl}
              name="autoCloseChecksOnScheduleFormSubmit"
              render={({ field: { value, onChange } }) => (
                <Switch id="auto-close-schedule-checks" checked={value} onCheckedChange={onChange} disabled={isAutoClosePending || !canEditWorkspaceSettings} />
              )}
            />
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="More information">
                    <QuestionIcon className="text-default-text h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent className="max-w-[290px]">
                    <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
                    When off, submitting a Schedule Check Form Record leaves linked schedule checks open for manual closure.
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {canEditWorkspaceSettings && (
          <Button type="button" variant="success" className="w-fit" onClick={handleSave} disabled={isSaveDisabled} loading={isSavePending}>
            Save changes
          </Button>
        )}
      </form>

      <SaveChangesDialog
        open={isConfirmOpen}
        onOpenChange={onConfirmOpenChange}
        saveCallback={() => settleConfirm(true)}
        discardCallback={() => settleConfirm(false)}
        loading={isSavePending}
        title="Save changes"
        description="This action is permanent and cannot be undone. Existing form records will remain, but the changes will impact your current forms and assets."
      />
    </>
  );
}

function SettingsSchedulesPage() {
  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Schedules</h1>
          <SettingsSchedulesForm />
        </div>
      </ScrollArea>
    </div>
  );
}

export default SettingsSchedulesPage;
